import React, { Component, type ErrorInfo, type ReactNode } from "react";

const DETAIL_MAX = 220;

function truncateDetail(s: string): string {
  const t = s.trim();
  if (!t) return "";
  return t.length > DETAIL_MAX ? `${t.slice(0, DETAIL_MAX - 1)}…` : t;
}

export type AppErrorBoundaryNavLink = { href: string; label: string };

export type AppErrorBoundaryProps = {
  children: ReactNode;
  /** Shown in console as `[AppErrorBoundary:…]` */
  surfaceLabel?: string;
  /** h1; when omitted, derived from `surfaceMessage` / root default */
  title?: string;
  /**
   * When set, this is the main user-facing line (prod + dev) for a **route** surface.
   * Omit for the top-level root boundary (keeps generic copy + dev long message).
   */
  surfaceMessage?: string;
  homeHref?: string;
  /** Extra nav links, e.g. Proof hub or XO Call */
  extraLinks?: AppErrorBoundaryNavLink[];
  /**
   * Show `Technical detail: …` from `err.message` when non-empty. Route surfaces default to on; root to off.
   */
  showTechnicalDetail?: boolean;
};

type State = { hasError: boolean; error: Error | null };

/**
 * Catches render/lifecycle and lazy child render failures.
 * useEffect / async: not caught; use try/catch in those paths.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, State> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, error: err };
  }

  componentDidCatch(err: Error, errorInfo: ErrorInfo): void {
    const label = (this.props.surfaceLabel || "root").trim() || "root";
    if (import.meta.env.DEV) {
      console.error(`[AppErrorBoundary:${label}]`, err, errorInfo);
    } else {
      console.error(`[AppErrorBoundary:${label}]`, err);
    }
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleFullReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    const err = this.state.error;
    const raw = err?.message != null ? String(err.message) : "";
    const detail = truncateDetail(raw);
    const isRouteSurface = Boolean(this.props.surfaceMessage);
    const showTech =
      Boolean(detail) && (isRouteSurface ? this.props.showTechnicalDetail !== false : this.props.showTechnicalDetail === true);
    const mainParagraph = this.props.surfaceMessage
      ? this.props.surfaceMessage
      : import.meta.env.DEV && raw
        ? raw
        : "Something went wrong. You can try again, or return home.";
    const title = this.props.title ?? (isRouteSurface ? "Surface error" : "Could not load this view");
    const home = (this.props.homeHref ?? "/").trim() || "/";

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#1E1B16",
          color: "#F6F0E6",
          fontFamily: "system-ui, sans-serif",
          padding: "1.5rem",
          textAlign: "center",
          boxSizing: "border-box",
        }}
      >
        <h1 style={{ fontSize: "1.1rem", fontWeight: 600, margin: "0 0 0.75rem" }}>{title}</h1>
        <p style={{ maxWidth: 480, fontSize: "0.9rem", lineHeight: 1.5, opacity: 0.9, margin: "0 0 0.65rem" }}>
          {mainParagraph}
        </p>
        {showTech ? (
          <p
            style={{
              maxWidth: 480,
              fontSize: "0.78rem",
              lineHeight: 1.45,
              opacity: 0.7,
              margin: "0 0 1rem",
              fontFamily: "ui-monospace, Menlo, monospace",
              wordBreak: "break-word",
            }}
          >
            Technical detail: {detail}
          </p>
        ) : null}
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "center" }}>
          {isRouteSurface ? (
            <button
              type="button"
              onClick={this.handleFullReload}
              style={{
                padding: "0.5rem 0.85rem",
                fontSize: "0.9rem",
                fontWeight: 500,
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(255,255,255,0.14)",
                color: "#F6F0E6",
                cursor: "pointer",
              }}
            >
              Refresh
            </button>
          ) : null}
          <button
            type="button"
            onClick={this.handleReset}
            style={{
              padding: "0.5rem 0.85rem",
              fontSize: "0.9rem",
              fontWeight: 500,
              borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "rgba(255,255,255,0.1)",
              color: "#F6F0E6",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
          <a
            href={home}
            style={{
              padding: "0.5rem 0.85rem",
              fontSize: "0.9rem",
              fontWeight: 500,
              borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#F6F0E6",
              textDecoration: "none",
            }}
          >
            Home
          </a>
          {isRouteSurface && this.props.extraLinks
            ? this.props.extraLinks.map((L) => (
                <a
                  key={L.href + L.label}
                  href={L.href}
                  style={{
                    padding: "0.5rem 0.85rem",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    borderRadius: 6,
                    border: "1px solid rgba(255,255,255,0.2)",
                    color: "#F6F0E6",
                    textDecoration: "none",
                  }}
                >
                  {L.label}
                </a>
              ))
            : null}
          {!isRouteSurface ? (
            <a
              href="/call"
              style={{
                padding: "0.5rem 0.85rem",
                fontSize: "0.9rem",
                fontWeight: 500,
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.2)",
                color: "#F6F0E6",
                textDecoration: "none",
              }}
            >
              XO Call
            </a>
          ) : null}
        </div>
      </div>
    );
  }
}


/** Preset for `/call` and related call app routes. */
export function CallRouteErrorBoundary({ children }: { children: ReactNode }): ReactNode {
  return (
    <AppErrorBoundary
      surfaceLabel="call"
      title="Call surface"
      surfaceMessage="Call surface could not be loaded."
      homeHref="/"
      extraLinks={[{ href: "/xo-proof", label: "Proof hub" }]}
    >
      {children}
    </AppErrorBoundary>
  );
}

/** Preset for `/xo-proof` and sub-routes. */
export function XoProofRouteErrorBoundary({ children }: { children: ReactNode }): ReactNode {
  return (
    <AppErrorBoundary
      surfaceLabel="proof"
      title="Proof hub"
      surfaceMessage="Proof hub could not be loaded."
      homeHref="/"
      extraLinks={[{ href: "/call", label: "XO Call" }]}
    >
      {children}
    </AppErrorBoundary>
  );
}
